// ============================================================================
// PHASE 6 - SECTION 1: A/B TESTING DASHBOARD (FRONTEND)
// Phoenix Rescue Apps - Life & Wealth Solutions
// Complete A/B testing management interface
// ============================================================================

import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { 
  Plus, Play, Pause, CheckCircle, TrendingUp, Activity, 
  Users, Target, AlertCircle, Edit, Trash2, Eye, FileText,
  Award, Clock, Settings, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ABTestingDashboard = () => {
  const [experiments, setExperiments] = useState([]);
  const [activeExperiment, setActiveExperiment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    experiment_type: 'message_variant',
    primary_metric: 'conversion_rate',
    duration_days: 14,
    variantA: 'Control — current version',
    variantB: 'Variant B — test version',
  });
  const [createSaved, setCreateSaved] = useState(false);

  // ========================================================================
  // DATA FETCHING
  // ========================================================================

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/ab-testing/dashboard`);
      const data = await response.json();
      
      if (data.success) {
        setDashboardStats(data.data);
        // Combine all experiments for the list
        const allExperiments = [
          ...data.data.running,
          ...data.data.completed,
          ...data.data.draft
        ];
        setExperiments(allExperiments);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API_URL}/ab-testing/experiments/${experimentId}`);
      const data = await response.json();
      
      if (data.success) {
        setActiveExperiment(data.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Error loading experiment:', error);
    }
  };

  // ========================================================================
  // EXPERIMENT ACTIONS
  // ========================================================================

  const startExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API_URL}/ab-testing/experiments/${experimentId}/start`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        loadDashboard();
        alert('Experiment started successfully!');
      }
    } catch (error) {
      console.error('Error starting experiment:', error);
      alert('Failed to start experiment');
    }
  };

  const pauseExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API_URL}/ab-testing/experiments/${experimentId}/pause`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        loadDashboard();
        alert('Experiment paused successfully!');
      }
    } catch (error) {
      console.error('Error pausing experiment:', error);
      alert('Failed to pause experiment');
    }
  };

  const completeExperiment = async (experimentId) => {
    try {
      const response = await fetch(`${API_URL}/ab-testing/experiments/${experimentId}/complete`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        loadDashboard();
        alert('Experiment completed successfully!');
      }
    } catch (error) {
      console.error('Error completing experiment:', error);
      alert('Failed to complete experiment');
    }
  };

  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================

  const getStatusColor = (status) => {
    const colors = {
      draft: 'gray',
      running: 'blue',
      paused: 'yellow',
      completed: 'green',
      archived: 'gray'
    };
    return colors[status] || 'gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <Edit size={16} />,
      running: <Activity size={16} className="animate-pulse" />,
      paused: <Pause size={16} />,
      completed: <CheckCircle size={16} />,
      archived: <FileText size={16} />
    };
    return icons[status] || <Activity size={16} />;
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredExperiments = experiments.filter(exp => {
    if (filterStatus === 'all') return true;
    return exp.status === filterStatus;
  });

  // ========================================================================
  // RENDER COMPONENTS
  // ========================================================================

  const renderDashboardStats = () => {
    if (!dashboardStats) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Running Experiments */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <Activity className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Running</p>
                <p className="text-2xl font-bold text-blue-900">
                  {dashboardStats.summary.running_count}
                </p>
              </div>
            </div>
          </div>
          <p className="text-sm text-blue-600">Active experiments collecting data</p>
        </div>

        {/* Completed Experiments */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <CheckCircle className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Completed</p>
                <p className="text-2xl font-bold text-green-900">
                  {dashboardStats.summary.completed_count}
                </p>
              </div>
            </div>
          </div>
          <p className="text-sm text-green-600">Experiments with results</p>
        </div>

        {/* Draft Experiments */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                <Edit className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Draft</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardStats.summary.draft_count}
                </p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600">Experiments in preparation</p>
        </div>
      </div>
    );
  };

  const renderExperimentCard = (experiment) => {
    const statusColor = getStatusColor(experiment.status);
    const statusIcon = getStatusIcon(experiment.status);

    return (
      <div key={experiment.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{experiment.name}</h3>
              <span className={`px-2 py-1 bg-${statusColor}-100 text-${statusColor}-800 text-xs font-medium rounded-full flex items-center space-x-1`}>
                {statusIcon}
                <span className="capitalize">{experiment.status}</span>
              </span>
            </div>
            <p className="text-sm text-gray-600">{experiment.description}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Variants</p>
            <p className="text-lg font-semibold text-gray-900">{experiment.variant_count || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Assignments</p>
            <p className="text-lg font-semibold text-gray-900">{experiment.assignment_count || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Events</p>
            <p className="text-lg font-semibold text-gray-900">{experiment.event_count || 0}</p>
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex items-center space-x-4 text-xs text-gray-500 mb-4">
          <div className="flex items-center space-x-1">
            <Target size={14} />
            <span className="capitalize">{experiment.experiment_type}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock size={14} />
            <span>{formatDate(experiment.start_date)}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          <button
            onClick={() => loadExperiment(experiment.id)}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Eye size={16} />
            <span>View Details</span>
          </button>

          {experiment.status === 'draft' && (
            <button
              onClick={() => startExperiment(experiment.id)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <Play size={16} />
            </button>
          )}

          {experiment.status === 'running' && (
            <>
              <button
                onClick={() => pauseExperiment(experiment.id)}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
              >
                <Pause size={16} />
              </button>
              <button
                onClick={() => completeExperiment(experiment.id)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <CheckCircle size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    const handleCreate = () => {
      if (!createForm.name.trim()) return;
      const newExp = {
        id: Date.now(),
        name: createForm.name,
        description: createForm.description,
        experiment_type: createForm.experiment_type,
        primary_metric: createForm.primary_metric,
        duration_days: createForm.duration_days,
        status: 'draft',
        variant_count: 2,
        assignment_count: 0,
        event_count: 0,
        start_date: null,
      };
      setExperiments(prev => [newExp, ...prev]);
      setCreateSaved(true);
      setTimeout(() => {
        setCreateSaved(false);
        setShowCreateModal(false);
        setCreateForm({ name: '', description: '', experiment_type: 'message_variant', primary_metric: 'conversion_rate', duration_days: 14, variantA: 'Control — current version', variantB: 'Variant B — test version' });
      }, 1200);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Create Experiment</h2>
            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Experiment Name *</label>
              <input
                value={createForm.name}
                onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Subject Line Test — Life Insurance"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={createForm.description}
                onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What are you testing and why?"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Experiment Type</label>
                <select
                  value={createForm.experiment_type}
                  onChange={e => setCreateForm(p => ({ ...p, experiment_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none"
                >
                  <option value="message_variant">Message Variant</option>
                  <option value="timing">Timing</option>
                  <option value="channel">Channel</option>
                  <option value="offer">Offer</option>
                  <option value="workflow">Workflow</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Primary Metric</label>
                <select
                  value={createForm.primary_metric}
                  onChange={e => setCreateForm(p => ({ ...p, primary_metric: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none"
                >
                  <option value="conversion_rate">Conversion Rate</option>
                  <option value="open_rate">Open Rate</option>
                  <option value="reply_rate">Reply Rate</option>
                  <option value="policy_bind_rate">Policy Bind Rate</option>
                  <option value="revenue">Revenue</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Duration (days)</label>
              <input
                type="number"
                min={1}
                max={90}
                value={createForm.duration_days}
                onChange={e => setCreateForm(p => ({ ...p, duration_days: Number(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Variant A (Control)</label>
                <input
                  value={createForm.variantA}
                  onChange={e => setCreateForm(p => ({ ...p, variantA: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Variant B (Test)</label>
                <input
                  value={createForm.variantB}
                  onChange={e => setCreateForm(p => ({ ...p, variantB: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleCreate}
              disabled={!createForm.name.trim()}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {createSaved ? '✓ Created!' : 'Create Experiment'}
            </button>
            <button
              onClick={() => setShowCreateModal(false)}
              className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDetailsModal = () => {
    if (!showDetailsModal || !activeExperiment) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{activeExperiment.name}</h2>
                <p className="text-sm text-gray-600 mt-1">{activeExperiment.description}</p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Configuration */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Primary Metric</p>
                  <p className="text-sm font-semibold text-gray-900 capitalize">
                    {activeExperiment.primary_metric.replace(/_/g, ' ')}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Sample Size</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {activeExperiment.minimum_sample_size}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Confidence Level</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {(activeExperiment.confidence_level * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Duration</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {activeExperiment.duration_days ? `${activeExperiment.duration_days} days` : 'Unlimited'}
                  </p>
                </div>
              </div>
            </div>

            {/* Variants */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Variants</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeExperiment.variants && activeExperiment.variants.map(variant => (
                  <div key={variant.id} className={`border-2 ${variant.is_control ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'} rounded-lg p-4`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{variant.variant_name}</h4>
                      {variant.is_control && (
                        <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded">
                          Control
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">Traffic: {variant.traffic_weight}%</p>
                    
                    {/* Variant Statistics */}
                    {activeExperiment.statistics && activeExperiment.statistics[variant.variant_key] && (
                      <div className="space-y-2">
                        {Object.entries(activeExperiment.statistics[variant.variant_key].metrics).map(([metricName, metricData]) => (
                          <div key={metricName} className="bg-white p-3 rounded border border-gray-200">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-500 capitalize">
                                {metricName.replace(/_/g, ' ')}
                              </p>
                              {metricData.is_significant && (
                                <Award size={12} className="text-green-500" />
                              )}
                            </div>
                            <p className="text-lg font-semibold text-gray-900">
                              {metricData.value.toFixed(2)}
                              {metricName.includes('rate') ? '%' : ''}
                            </p>
                            {metricData.lift_percentage !== null && !variant.is_control && (
                              <p className={`text-xs ${metricData.lift_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {metricData.lift_percentage > 0 ? '+' : ''}{metricData.lift_percentage.toFixed(2)}% vs control
                              </p>
                            )}
                            <p className="text-xs text-gray-500">n = {metricData.sample_size}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Winner */}
            {activeExperiment.winner_variant_id && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                <div className="flex items-center space-x-3 mb-2">
                  <Award size={24} className="text-green-600" />
                  <h3 className="text-lg font-semibold text-green-900">Winner Declared</h3>
                </div>
                <p className="text-sm text-green-700">
                  Variant: {activeExperiment.variants.find(v => v.id === activeExperiment.winner_variant_id)?.variant_name}
                </p>
                <p className="text-sm text-green-700">
                  Statistical Significance: {activeExperiment.statistical_significance ? 'Yes' : 'No'}
                </p>
                {activeExperiment.p_value && (
                  <p className="text-sm text-green-700">
                    P-value: {activeExperiment.p_value.toFixed(6)}
                  </p>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-4">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
              {activeExperiment.status === 'running' && (
                <button
                  onClick={() => {
                    completeExperiment(activeExperiment.id);
                    setShowDetailsModal(false);
                  }}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center space-x-2"
                >
                  <CheckCircle size={16} />
                  <span>Complete Experiment</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Activity className="animate-spin mx-auto mb-4" size={48} />
          <p className="text-gray-600">Loading A/B Testing Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">A/B Testing Dashboard</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>Create Experiment</span>
          </button>
        </div>
        <p className="text-gray-600">Optimize your automation workflows and user experiences</p>
      </div>

      {/* Dashboard Stats */}
      {renderDashboardStats()}

      {/* Filters */}
      <div className="mb-6 flex items-center space-x-4">
        <Filter size={20} className="text-gray-400" />
        <div className="flex space-x-2">
          {['all', 'running', 'completed', 'draft', 'paused'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="capitalize">{status}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Experiments List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredExperiments.length > 0 ? (
          filteredExperiments.map(renderExperimentCard)
        ) : (
          <div className="col-span-2 text-center py-12">
            <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-gray-600">No experiments found</p>
            <p className="text-sm text-gray-500 mt-2">Create your first experiment to get started</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {renderCreateModal()}
      {renderDetailsModal()}
    </div>
  );
};

export default ABTestingDashboard;
